import React, { useState } from 'react';
import { FloatingHearts } from '../components/FloatingHearts';
import { HeartIcon } from 'lucide-react';
export function ValentinePage() {
  const [accepted, setAccepted] = useState(false);
  const [noCount, setNoCount] = useState(0);
  const [noPosition, setNoPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const handleYes = () => {
    setAccepted(true);
  };
  const handleNo = () => {
    setNoCount(prev => prev + 1);
    // Move it again even if somehow clicked
    moveNoButton();
  };
  const moveNoButton = () => {
    const padding = 80;
    const x = padding + Math.random() * (window.innerWidth - padding * 2);
    const y = padding + Math.random() * (window.innerHeight - padding * 2);
    setNoPosition({
      x,
      y
    });
    setNoCount(prev => prev + 1);
  };
  // Dynamic text for the "No" button if they keep clicking it
  const getNoText = () => {
    const texts = ['Are you sure?', 'Really sure?', 'Think again!', "Maybe you didn't drink enough water today?", 'Last chance!', 'Pretty please? 🥺', 'Just say yes!', "I'll wait... ⏳"];
    if (noCount === 0) return 'No';
    return texts[(noCount - 1) % texts.length];
  };
  // Background pattern SVG (encoded)
  // A subtle heart pattern
  const bgPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 35.45C26.5 32 15 22 15 16c0-3.5 2.5-6 6-6 2 0 4 1 5.5 3C28 11 30 13 30 13s2-2 3.5-4C35 7 37 6 39 6c3.5 0 6 2.5 6 6 0 6-11.5 16-15 19.45z' fill='%233a0d1c' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`;
  return <div className="h-screen w-full flex flex-col items-center justify-center relative overflow-hidden transition-colors duration-1000" style={{
    backgroundColor: '#1a0510',
    backgroundImage: bgPattern,
    fontFamily: "'Poppins', sans-serif"
  }} data-id="element-5">
      {/* Font Imports */}
      <style data-id="element-6">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Poppins:wght@300;400;600&display=swap');
          
          .font-script {
            font-family: 'Great Vibes', cursive;
          }
          
          .animate-pulse-slow {
            animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: .8; transform: scale(1.05); }
          }

          @keyframes glitter {
            0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
            50% { opacity: 1; transform: scale(1) rotate(180deg); }
          }

          .glitter-container {
            position: relative;
            display: inline-block;
          }

          .glitter-spark {
            position: absolute;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: white;
            box-shadow: 0 0 6px 2px rgba(255, 182, 213, 0.8), 0 0 12px 4px rgba(255, 182, 213, 0.4);
            animation: glitter 2s ease-in-out infinite;
            pointer-events: none;
          }
        `}
      </style>

      <FloatingHearts data-id="element-7" />

      <div className="z-10 max-w-2xl w-full px-4 flex flex-col items-center text-center" data-id="element-8">
        {!accepted ? <>
            {/* Image with Glow */}
            <div className="relative mb-6 group" data-id="element-9">
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000" data-id="element-10"></div>
              <div className="relative rounded-2xl overflow-hidden border-4 border-pink-900/50 shadow-2xl" data-id="element-11">
                <img src="https://cdn.magicpatterns.com/uploads/gAkm1kTXTerGQPZnQapcBv/FullSizeRender.jpg" alt="Josephine" className="w-full max-w-[200px] md:max-w-xs object-cover" data-id="element-12" />
              </div>

              {/* Decorative floating elements around image */}
              <HeartIcon className="absolute -top-6 -right-6 w-12 h-12 text-pink-500 animate-bounce" fill="currentColor" data-id="element-13" />
              <HeartIcon className="absolute -bottom-4 -left-4 w-8 h-8 text-pink-400 animate-pulse" fill="currentColor" data-id="element-14" />
            </div>

            {/* Text Content */}
            <div className="glitter-container w-full px-2" data-id="element-15">
              <h1 className="font-script text-5xl sm:text-6xl md:text-8xl text-pink-200 mb-1 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] animate-pulse-slow py-4 leading-relaxed" style={{
            background: 'linear-gradient(to right, #f9a8d4, #fce7f3, #f9a8d4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            paddingBottom: '0.15em',
            lineHeight: '1.4'
          }} data-id="element-16">
                Josephine
              </h1>
              {/* Glitter sparkles */}
              <span className="glitter-spark" style={{
            top: '10%',
            left: '5%',
            animationDelay: '0s'
          }} data-id="element-17" />
              <span className="glitter-spark" style={{
            top: '20%',
            right: '8%',
            animationDelay: '0.4s'
          }} data-id="element-18" />
              <span className="glitter-spark" style={{
            top: '60%',
            left: '15%',
            animationDelay: '0.8s'
          }} data-id="element-19" />
              <span className="glitter-spark" style={{
            top: '30%',
            right: '20%',
            animationDelay: '1.2s'
          }} data-id="element-20" />
              <span className="glitter-spark" style={{
            top: '70%',
            left: '40%',
            animationDelay: '0.2s'
          }} data-id="element-21" />
              <span className="glitter-spark" style={{
            top: '15%',
            left: '55%',
            animationDelay: '1.6s'
          }} data-id="element-22" />
              <span className="glitter-spark" style={{
            top: '50%',
            right: '12%',
            animationDelay: '0.6s'
          }} data-id="element-23" />
              <span className="glitter-spark" style={{
            top: '80%',
            right: '35%',
            animationDelay: '1s'
          }} data-id="element-24" />
            </div>

            <p className="text-lg md:text-2xl text-pink-200/90 mb-8 font-light tracking-wide" data-id="element-25">
              Will you be my valentine?
            </p>

            {/* Buttons */}
            <div className="flex flex-col items-center gap-5 w-full" data-id="element-26">
              <button onClick={handleYes} className="group relative w-full max-w-xs px-8 py-4 rounded-[16px] text-white text-xl font-bold shadow-[0_0_20px_rgba(255,203,224,0.5)] hover:shadow-[0_0_30px_rgba(255,203,224,0.8)] transform transition-all duration-300 hover:-translate-y-1 hover:scale-105 active:scale-95 overflow-hidden" style={{
            backgroundColor: '#FFCBE0'
          }} data-id="element-27">
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-28">
                  <HeartIcon className="w-6 h-6 animate-pulse" fill="currentColor" data-id="element-29" />
                  YES
                </span>
              </button>

              {/* No button - starts inline, escapes to random positions on hover/touch */}
              {!noPosition ? <button onClick={handleNo} onMouseEnter={moveNoButton} onTouchStart={moveNoButton} className="z-50 px-6 py-2 bg-gray-800/50 hover:bg-gray-800 text-gray-400 text-sm rounded-full border border-gray-700 hover:border-gray-600 whitespace-nowrap" data-id="element-30">
                  {getNoText()}
                </button> : <div className="h-10" data-id="element-31" />}
            </div>
          </> : <div className="flex flex-col items-center animate-in fade-in zoom-in duration-700" data-id="element-32">
            <div className="relative" data-id="element-33">
              <div className="absolute inset-0 bg-pink-500 blur-[60px] opacity-20 animate-pulse" data-id="element-34"></div>
              <img src="https://cdn.magicpatterns.com/uploads/jUMQpkEkBrgfixkFyAEeWG/Gemini_Generated_Image_4x5o464x5o464x5o.png" alt="Josephine" className="relative w-80 h-80 md:w-96 md:h-96 object-cover rounded-full border-4 border-pink-400 shadow-[0_0_30px_rgba(236,72,153,0.6)]" data-id="element-35" />
            </div>

            <div className="mt-6 flex gap-2" data-id="element-36">
              {[...Array(5)].map((_, i) => <HeartIcon key={i} className="w-8 h-8 text-pink-500 animate-bounce" fill="currentColor" style={{
            animationDelay: `${i * 0.1}s`
          }} data-id="element-37" />)}
            </div>
          </div>}
      </div>

      {/* No button - fixed position after first dodge */}
      {!accepted && noPosition && <button onClick={handleNo} onMouseEnter={moveNoButton} onTouchStart={moveNoButton} className="z-50 px-6 py-2 bg-gray-800/50 hover:bg-gray-800 text-gray-400 text-sm rounded-full border border-gray-700 hover:border-gray-600 whitespace-nowrap" style={{
      position: 'fixed',
      left: `${noPosition.x}px`,
      top: `${noPosition.y}px`,
      transform: 'translate(-50%, -50%)',
      transition: 'left 0.3s ease-out, top 0.3s ease-out'
    }} data-id="element-38">
          {getNoText()}
        </button>}
    </div>;
}